/**
 * Testimonials: keep the drift, but let people take the wheel.
 *
 * The row used to be a pure CSS marquee — a track with `width: max-content`
 * moved by `transform: translateX(...)`. Because the movement was a transform,
 * the viewport had nothing to scroll: you could watch the cards go by but never
 * reach for one, and the only way to read a card that had just passed was to
 * wait a full lap. Manual scrolling existed solely behind
 * `prefers-reduced-motion`.
 *
 * The transform is gone. The viewport is a real horizontal scroller now, and the
 * drift is the same scroller being nudged along a fraction of a pixel per frame.
 * Auto and manual are therefore the same mechanism and cannot fight each other:
 * a swipe, a trackpad flick, a drag of the scrollbar or a keyboard tab all just
 * move the thing that was already moving.
 *
 * The track renders several identical copies of the cards, so looping is a
 * matter of keeping the scroll offset inside one copy: add or subtract a whole
 * copy width and you land on a pixel-identical frame. Two things make that hold
 * up on a phone (reported 11-09-2026: on iOS the row stopped at the last card
 * instead of starting over):
 *
 *   1. The offset is parked in the MIDDLE copy, not the first one. That leaves a
 *      full copy of runway on both sides, so a hard flick runs out of momentum
 *      before it reaches the physical end of the scroller — no rubber band, and
 *      the row never appears to stop.
 *   2. Safari ignores writes to `scrollLeft` while its momentum animation owns
 *      the scroller, so a correction made during the fling is dropped. The
 *      correction is therefore repeated on `touchend`, `scrollend` and `scroll`,
 *      which is where the fling actually lands.
 *
 * It holds still when a person is busy with it (pointer down, wheel, hover,
 * keyboard focus) and for a few seconds after, when the section is off screen,
 * and entirely when the visitor asks for reduced motion.
 */
(function () {
  'use strict';

  const RESUME_AFTER_MS = 2500;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function setup(viewport) {
    const track = viewport.querySelector('.brand-reviews__marquee');
    if (!track || viewport.dataset.sbScroller === 'on') return;
    viewport.dataset.sbScroller = 'on';

    const copies = Math.max(1, parseInt(track.style.getPropertyValue('--brand-reviews-copies'), 10) || 2);

    // The old animation crossed one copy in `animation-duration`. Deriving the
    // speed from the same numbers keeps the pace the shop already had.
    const seconds = parseFloat(track.style.animationDuration) || 90;

    let paused = 0; // timestamp until which the drift stays parked
    let visible = true;
    let last = null;
    let touching = false;

    const copyWidth = () => track.scrollWidth / copies;
    const pause = () => { paused = performance.now() + RESUME_AFTER_MS; };

    /* Where the offset is kept: one copy in when the track is long enough to
       spare it, otherwise the first copy, which is then all there is. */
    function base(width) {
      const maxScroll = viewport.scrollWidth - viewport.clientWidth;
      return maxScroll >= width * 2 ? width : 0;
    }

    /* Bring the offset back inside that one copy, however far it was flung. */
    function normalise() {
      const width = copyWidth();
      if (!(width > 0)) return;
      const lo = base(width);
      const x = viewport.scrollLeft;
      if (x >= lo && x < lo + width) return;
      const laps = Math.floor((x - lo) / width);
      if (laps !== 0) viewport.scrollLeft = x - laps * width;
    }

    for (const type of ['pointerdown', 'touchstart', 'wheel', 'mouseenter', 'focusin']) {
      viewport.addEventListener(type, pause, { passive: true });
    }

    viewport.addEventListener('touchstart', () => { touching = true; }, { passive: true });
    for (const type of ['touchend', 'touchcancel']) {
      viewport.addEventListener(type, () => { touching = false; normalise(); }, { passive: true });
    }
    // `scrollend` fires once the fling has settled, which is the moment Safari
    // accepts a write again. Not every browser has it, hence plain `scroll` too.
    viewport.addEventListener('scrollend', normalise, { passive: true });
    viewport.addEventListener('scroll', function () { if (!touching) normalise(); }, { passive: true });

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => { for (const entry of entries) visible = entry.isIntersecting; },
        { threshold: 0 }
      ).observe(viewport);
    }

    function frame(now) {
      window.requestAnimationFrame(frame);

      /* Off screen: do nothing at all, not even measure.
         `copyWidth()` and `normalise()` both read `scrollWidth`, and a read like
         that forces the browser to lay the page out before it can answer. Doing
         that 60x a second for a section nobody is looking at is not free: on a
         throttled phone this loop alone accounted for 700 ms of main-thread time
         during page load, while the testimonials were still far below the fold
         (profiled 18-09-2026). The row only needs correcting while it is in view,
         because that is the only time it can be seen to be wrong.
         `last` is cleared so the first frame back does not apply a huge delta. */
      if (!visible) {
        last = null;
        return;
      }

      const delta = last === null ? 0 : now - last;
      last = now;

      const width = copyWidth();

      if (width > 0) {
        if (!touching) normalise();

        const idle = now >= paused;
        if (idle && !reduceMotion.matches && delta > 0 && delta < 200) {
          viewport.scrollLeft += (width / (seconds * 1000)) * delta;
        }
      }
    }

    // Start inside the middle copy rather than at the very left, so a first
    // swipe backwards already has a card to show.
    const startWidth = copyWidth();
    if (startWidth > 0) viewport.scrollLeft = base(startWidth);

    window.requestAnimationFrame(frame);
  }

  function init() {
    document.querySelectorAll('.brand-reviews__viewport').forEach(setup);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  // The theme editor re-renders sections in place.
  document.addEventListener('shopify:section:load', init);
})();
